import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';

const CaseStudiesPage = () => {
  const { t } = useTranslation();
  const { caseStudies } = useMockData();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-6">
              {t('caseStudies.pageTitle')}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('caseStudies.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <div 
                key={study.id} 
                id={study.id}
                className="scroll-mt-32"
              >
                <div className="grid lg:grid-cols-3 gap-12">
                  {/* Left - Summary */}
                  <div className="lg:col-span-1">
                    <span className="inline-block px-4 py-2 bg-[#00FFD1]/10 text-[#00FFD1] text-sm font-medium mb-6">
                      {study.industry}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
                      {study.title}
                    </h2>
                    <p className="text-white/70 mb-8">{study.client}</p>
                    
                    {/* Results Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="p-4 bg-white/[0.02] border border-white/10">
                          <div className="text-2xl font-semibold text-[#00FFD1] mb-1">
                            {result.value}
                          </div>
                          <div className="text-xs text-white/70 uppercase tracking-wider">
                            {result.metric}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Right - Details */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Context */}
                    <div>
                      <h3 className="text-sm text-white/70 uppercase tracking-wider mb-3">
                        {t('caseStudies.context')}
                      </h3>
                      <p className="text-white/70 leading-relaxed">{study.context}</p>
                    </div>
                    
                    {/* Challenge */}
                    <div>
                      <h3 className="text-sm text-white/70 uppercase tracking-wider mb-3">
                        {t('caseStudies.challenge')}
                      </h3>
                      <p className="text-white/70 leading-relaxed">{study.challenge}</p>
                    </div>
                    
                    {/* Solution */}
                    <div>
                      <h3 className="text-sm text-white/70 uppercase tracking-wider mb-3">
                        {t('caseStudies.solution')}
                      </h3>
                      <p className="text-white/70 leading-relaxed">{study.solution}</p>
                    </div>
                    
                    {/* Tech Stack */}
                    <div>
                      <h3 className="text-sm text-white/70 uppercase tracking-wider mb-3">
                        {t('caseStudies.techStack')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {study.techStack.map((tech) => (
                          <span 
                            key={tech} 
                            className="px-4 py-2 bg-white/5 border border-white/10 text-white/80 text-sm font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Divider */}
                {index < caseStudies.length - 1 && (
                  <div className="border-b border-white/10 mt-24" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
                {t('caseStudies.yourProject')}
              </h2>
              <p className="text-lg text-white/60">
                {t('caseStudies.yourProjectDesc')}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link to="/contact">
                <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto">
                  {t('caseStudies.startConversation')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="bg-transparent text-white border border-white/20 hover:bg-white hover:text-black px-8 py-4 text-lg font-medium h-auto">
                  {t('caseStudies.exploreServices')}
                  <ArrowUpRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CaseStudiesPage;
